import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import { api } from 'redux-store/api';
import { IPhase } from 'redux-store/types';
import {
  GetTemplateChangesArgs,
  IGetAllProjectsArgs,
  IGetOverdueItemsArgs,
  IGetOverdueItemsResponse,
  IGetPhasesArgs,
  IGetProjectByIdArgs,
  IGetProjectProfilesManagementArgs,
  IGetProjectsArgs,
  IGetUnviewedCountsArgs,
  IGetUnviewedItemsResponse,
  IProject,
  IProjectProfilesManagement,
  IProjectShort,
  IUpdateProjectBody,
  IUpdateProjectTemplateBody,
  TGetProfileAllProjectsResponse,
  TGetProjectsResponse,
  TemplateChanges,
  UpdateTemplateBody,
} from './';
import { ISetContactPersonArgs, ISetContactPersonResponse } from './projects.types';
// slices
import { isErrorWithData } from 'redux-store/helpers/is-error-with-data';
import { projectByIdMapping } from 'redux-store/mappings/project-by-id';
import { handleSetSucceeded, handleShowRefetchLoader } from 'redux-store/slices/manage-gantt-slice';
import {
  setProjectPhaseProfileCollapsedState,
  updateProjectPhaseCollapsedState,
} from 'redux-store/slices/project-phase-profile-metadata-slice';

enum Endpoints {
  PROJECTS_V2 = 'v2/projects/',
  PROJECTS = 'projects/',
}

const PROJECTS_SERIALIZATION_KEY = 1;
export const UNVIEWED_ITEMS_SERIALIZATION_KEY = 2;

export const projectsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProfileAllProjects: build.query<TGetProfileAllProjectsResponse, void>({
      query: () => ({
        url: `${Endpoints.PROJECTS_V2}all-for-schedule`,
      }),
      providesTags: ['Projects'],
    }),

    getProjects: build.query<TGetProjectsResponse, IGetProjectsArgs>({
      query: (queryArgs) => ({
        url: Endpoints.PROJECTS_V2,
        params: queryArgs,
      }),
      serializeQueryArgs: () => {
        return PROJECTS_SERIALIZATION_KEY;
      },
      merge: (currentCache, newCache, { arg: { page } }) => {
        if (page === 1) {
          return newCache;
        }
        return {
          meta: newCache.meta,
          items: [...currentCache.items, ...newCache.items],
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return !isEqual(currentArg, previousArg);
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          data.items.forEach((project) => {
            const phasesCollapsedState: Record<number, boolean> = {};

            project.phases?.forEach((phase) => {
              phasesCollapsedState[phase.id] = Boolean(phase?.profileMetadata?.isCollapsed);
            });

            dispatch(
              setProjectPhaseProfileCollapsedState({
                projectId: project.id,
                collapsedState: phasesCollapsedState,
              })
            );
          });
        } catch {}
      },
      keepUnusedDataFor: 0,
    }),

    getProjectById: build.query<IProject, IGetProjectByIdArgs>({
      query: ({ id }) => ({
        url: `${Endpoints.PROJECTS_V2}${id}`,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(handleSetSucceeded(true));
          dispatch(handleShowRefetchLoader(false));
          dispatch(
            projectsApi.util.updateQueryData(
              'getProjects',
              PROJECTS_SERIALIZATION_KEY as IGetProjectsArgs,
              (draft) => {
                if (draft?.items) {
                  const index = draft.items.findIndex(({ id }) => response.id === id);
                  if (index >= 0) {
                    Object.assign(draft.items[index], response);
                  }
                }
              }
            )
          );

          const phasesCollapsedState: Record<number, boolean> = {};

          response.phases?.forEach((phase) => {
            phasesCollapsedState[phase.id] = Boolean(phase?.profileMetadata?.isCollapsed);
          });

          dispatch(
            setProjectPhaseProfileCollapsedState({
              projectId: response.id,
              collapsedState: phasesCollapsedState,
            })
          );
        } catch {
          dispatch(handleSetSucceeded(false));
        }
      },
      providesTags: (_, __, { id }) => [{ type: 'Projects', id: Number(id) }],

      transformResponse: (response: IProject) => {
        return projectByIdMapping(response);
      },
    }),

    getAllProjects: build.query<IProjectShort[], IGetAllProjectsArgs>({
      query: ({ profileId }) => ({
        url: `${Endpoints.PROJECTS_V2}${profileId}/all`,
      }),
    }),

    archiveAllProjects: build.mutation({
      query: () => ({
        url: `${Endpoints.PROJECTS_V2}archive/all`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Current Plan'],
    }),

    updateProjectPhaseProfileMetadata: build.mutation<
      { success: boolean },
      { phaseId: number; isCollapsed: boolean; projectId: number }
    >({
      query: ({ phaseId, isCollapsed }) => ({
        url: `${Endpoints.PROJECTS_V2}phases/${phaseId}/metadata`,
        method: 'PATCH',
        body: { isCollapsed },
      }),
      async onQueryStarted({ phaseId, isCollapsed, projectId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            updateProjectPhaseCollapsedState({
              projectId,
              phaseId,
              isCollapsed,
            })
          );
        } catch {}
      },
    }),

    getPhases: build.query<IPhase[], IGetPhasesArgs>({
      query: ({ projectId }) => ({
        url: `${Endpoints.PROJECTS_V2}${projectId}/phases`,
      }),
    }),

    getUnviewedCounts: build.query<IGetUnviewedItemsResponse, IGetUnviewedCountsArgs>({
      query: ({ projectIds }) => ({
        url: `${Endpoints.PROJECTS_V2}unviewed-counts?${queryString.stringify(
          { projectIds },
          { arrayFormat: 'bracket' }
        )}`,
      }),
      serializeQueryArgs: () => {
        return { UNVIEWED_ITEMS_SERIALIZATION_KEY };
      },
      merge: (currentCache, newCache) => {
        return {
          items: { ...currentCache.items, ...newCache.items },
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        return !isEqual(currentArg, previousArg);
      },

      providesTags: (result, error, arg) => {
        if (!arg?.projectIds?.length) return [];
        return arg.projectIds.map((id) => ({ type: 'Unviewed Counts', id }));
      },
    }),

    getProjectsProfilesManagement: build.query<
      IProjectProfilesManagement,
      IGetProjectProfilesManagementArgs
    >({
      query: ({ id }) => ({
        url: `${Endpoints.PROJECTS}${id}/profiles-management`,
      }),
      providesTags: (result, _, args) => (result ? [{ type: 'Projects', id: args?.id }] : []),
    }),

    createProject: build.mutation<IProject, FormData>({
      query: (data) => ({
        url: Endpoints.PROJECTS_V2,
        body: data,
        method: 'POST',
      }),
      invalidatesTags: ['Current Plan'],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            projectsApi.util.updateQueryData(
              'getProjects',
              PROJECTS_SERIALIZATION_KEY as IGetProjectsArgs,
              (draft) => {
                if (draft?.items) {
                  draft.items.unshift(response);
                }
              }
            )
          );
        } catch {}
      },
    }),

    updateProject: build.mutation<IProject, IUpdateProjectBody>({
      query: ({ data, id }) => ({
        url: `${Endpoints.PROJECTS_V2}${id}`,
        body: data,
        method: 'PATCH',
      }),
      onQueryStarted: async ({ withoutRefetching, id }, { dispatch, queryFulfilled }) => {
        try {
          const { data: response } = await queryFulfilled;
          if (!withoutRefetching) {
            dispatch(
              projectsApi.util.updateQueryData(
                'getProjects',
                PROJECTS_SERIALIZATION_KEY as IGetProjectsArgs,
                (draft) => {
                  if (draft?.items?.length) {
                    const index = draft.items.findIndex(({ id: projectId }) => id === projectId);
                    if (
                      index >= 0 &&
                      draft.items.some(({ status }) => status !== response?.status)
                    ) {
                      draft.items = draft.items.filter(({ id: pId }) => pId !== response?.id);
                    } else if (index >= 0) {
                      Object.assign(draft.items[index], response);
                    }
                  }
                }
              )
            );
            dispatch(
              projectsApi.util.updateQueryData('getProjectById', { id }, (draft) => {
                if (draft) {
                  Object.assign(draft, response);
                }
              })
            );
          }
        } catch (e) {
          const error = e?.error;
          if (isErrorWithData(error)) {
            if (error.status === 404) {
              if (!withoutRefetching) {
                dispatch(
                  projectsApi.util.updateQueryData(
                    'getProjects',
                    PROJECTS_SERIALIZATION_KEY as IGetProjectsArgs,
                    (draft) => {
                      if (draft?.items?.length) {
                        draft.items = draft.items.filter((project) => project.id !== id);
                      }
                    }
                  )
                );
              }
            }
          }
        }
      },
      invalidatesTags: (result, _, { withoutRefetching }) =>
        result && !withoutRefetching ? [{ type: 'Projects', id: result?.id }, 'Current Plan'] : [],
    }),

    updateProjectTemplate: build.mutation<IProject, IUpdateProjectTemplateBody>({
      query: ({ projectId, ...data }) => ({
        url: `${Endpoints.PROJECTS_V2}${projectId}/change-template`,
        body: data,
        method: 'PATCH',
      }),
      onQueryStarted: async ({ projectId: templateProjectId }, { dispatch, queryFulfilled }) => {
        try {
          const { data: response } = await queryFulfilled;
          dispatch(
            projectsApi.util.updateQueryData(
              'getProjects',
              PROJECTS_SERIALIZATION_KEY as IGetProjectsArgs,
              (draft) => {
                if (draft?.items?.length) {
                  const index = draft.items.findIndex(
                    ({ id: projectId }) => templateProjectId === projectId
                  );
                  if (index >= 0 && draft.items.some(({ status }) => status !== response?.status)) {
                    draft.items = draft.items.filter(({ id: pId }) => pId !== response?.id);
                  } else if (index >= 0) {
                    Object.assign(draft.items[index], response);
                  }
                }
              }
            )
          );
          dispatch(
            projectsApi.util.updateQueryData(
              'getProjectById',
              { id: templateProjectId },
              (draft) => {
                if (draft) {
                  Object.assign(draft, response);
                }
              }
            )
          );
        } catch {}
      },
      invalidatesTags: (result) =>
        result ? [{ type: 'Projects', id: result?.id }, 'Current Plan'] : [],
    }),

    getTemplateChangesCount: build.query<TemplateChanges, GetTemplateChangesArgs>({
      query: ({ templateId, projectId }) => ({
        url: `${Endpoints.PROJECTS_V2}template-changes/${templateId}/${projectId}`,
      }),
      keepUnusedDataFor: 0,
    }),

    saveAsTemplate: build.mutation<TemplateChanges, UpdateTemplateBody>({
      query: ({ projectId, ...data }) => ({
        url: `${Endpoints.PROJECTS_V2}${projectId}/save-as-template`,
        body: data.data,
        method: 'PATCH',
      }),
      invalidatesTags: ['Template', 'Projects'],
    }),

    getOverdueItems: build.query<IGetOverdueItemsResponse, IGetOverdueItemsArgs>({
      query: ({ projectId }) => ({
        url: `${Endpoints.PROJECTS_V2}${projectId}/overdue`,
      }),
      keepUnusedDataFor: 0,
    }),

    setContactPerson: build.mutation<ISetContactPersonResponse, ISetContactPersonArgs>({
      query: ({ projectId, profileId, isContactPerson = true }) => ({
        url: `${Endpoints.PROJECTS_V2}contact-person`,
        method: 'PATCH',
        body: { projectId, profileId, isContactPerson },
      }),
      invalidatesTags: (result, error, { projectId }) => [{ type: 'Projects', id: projectId }],
    }),
  }),
});

export const {
  useGetProfileAllProjectsQuery,
  useLazyGetProfileAllProjectsQuery,
  useGetProjectsQuery,
  useLazyGetProjectsQuery,
  useArchiveAllProjectsMutation,
  useGetPhasesQuery,
  useLazyGetPhasesQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useGetProjectsProfilesManagementQuery,
  useLazyGetProjectsProfilesManagementQuery,
  useUpdateProjectTemplateMutation,
  useGetProjectByIdQuery,
  useLazyGetProjectByIdQuery,
  useGetUnviewedCountsQuery,
  useLazyGetUnviewedCountsQuery,
  useGetAllProjectsQuery,
  useLazyGetAllProjectsQuery,
  useUpdateProjectPhaseProfileMetadataMutation,
  useGetTemplateChangesCountQuery,
  useSaveAsTemplateMutation,
  useGetOverdueItemsQuery,
  useLazyGetOverdueItemsQuery,
  useSetContactPersonMutation,
} = projectsApi;
